// @oasis-permission-public: open to any signed-in Oasis user
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { ensureSession } from "~/modules/auth/credentials";
import {
  pollYoutubeIngestMutation,
  submitYoutubeIngestMutation,
} from "~/modules/youtube-download/network/mutations";
import type {
  PollYoutubeIngestResponse,
  YoutubeIngestWorkflowResult,
  YoutubeIngestWorkflowState,
} from "~/modules/youtube-download/network/schemas";

export const Route = createFileRoute("/_dash/youtube-download")({
  component: RouteComponent,
  beforeLoad() {
    ensureSession();
  },
});

const assetCdnBaseUrl = import.meta.env.VITE_ASSET_CDN_BASE_URL;

type DownloadState =
  | { status: "idle" }
  | { status: "submitting" }
  | {
      status: "processing";
      workflowExecutionId: string;
      workflowState: YoutubeIngestWorkflowState;
    }
  | {
      status: "completed";
      workflowExecutionId: string;
      result: YoutubeIngestWorkflowResult;
    }
  | {
      status: "failed";
      workflowExecutionId?: string;
      error: string;
    };

type PollUntilCompleteResult =
  | {
      status: "completed";
      response: PollYoutubeIngestResponse;
      result: YoutubeIngestWorkflowResult;
    }
  | {
      status: "failed";
      response?: PollYoutubeIngestResponse;
      error: string;
    };

const parseOptionalSeconds = (value: string, label: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) {
    return;
  }

  const parsed = Number.parseFloat(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) {
    throw new Error(`${label} must be a positive number`);
  }

  return parsed;
};

const buildAssetUrl = (assetId: string): string | null => {
  if (!assetCdnBaseUrl) {
    return null;
  }
  const base = assetCdnBaseUrl.endsWith("/") ? assetCdnBaseUrl.slice(0, -1) : assetCdnBaseUrl;
  return `${base}/${assetId}/video.mp4`;
};

function RouteComponent() {
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [startTimeSeconds, setStartTimeSeconds] = useState("");
  const [endTimeSeconds, setEndTimeSeconds] = useState("");
  const [downloadState, setDownloadState] = useState<DownloadState>({ status: "idle" });
  const [latestPollResponse, setLatestPollResponse] = useState<PollYoutubeIngestResponse | null>(
    null
  );

  const { mutateAsync: submitWorkflow, isPending: submitPending } =
    submitYoutubeIngestMutation.useMutation();
  const { mutateAsync: pollWorkflow } = pollYoutubeIngestMutation.useMutation();

  const isWorking = downloadState.status === "submitting" || downloadState.status === "processing";
  const canSubmit = Boolean(youtubeUrl.trim()) && !isWorking;

  const pollUntilComplete = useCallback(
    async (workflowExecutionId: string): Promise<PollUntilCompleteResult> => {
      const pollIntervalMs = 3000;
      const maxAttempts = 400;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const response = await pollWorkflow({
          params: { workflowExecutionId },
        });

        setLatestPollResponse(response);

        if (response.state === "COMPLETED") {
          if (response.result) {
            return {
              status: "completed",
              response,
              result: response.result,
            };
          }
          return {
            status: "failed",
            response,
            error: "Workflow completed without output data",
          };
        }

        if (response.state === "FAILED" || response.state === "CANCELLED") {
          return {
            status: "failed",
            response,
            error: response.error ?? "Workflow failed",
          };
        }

        setDownloadState({
          status: "processing",
          workflowExecutionId,
          workflowState: response.state,
        });

        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
      }

      return {
        status: "failed",
        error: "Workflow timed out before completion",
      };
    },
    [pollWorkflow]
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const trimmedUrl = youtubeUrl.trim();
      if (!trimmedUrl) {
        toast.error("YouTube URL is required");
        return;
      }

      try {
        const parsedStartTime = parseOptionalSeconds(startTimeSeconds, "Start time");
        const parsedEndTime = parseOptionalSeconds(endTimeSeconds, "End time");

        if (parsedStartTime != null && parsedEndTime != null && parsedEndTime <= parsedStartTime) {
          toast.error("End time must be greater than start time");
          return;
        }

        setLatestPollResponse(null);
        setDownloadState({ status: "submitting" });

        const { workflowExecutionId } = await submitWorkflow({
          params: {
            youtubeUrl: trimmedUrl,
            startTimeSeconds: parsedStartTime,
            endTimeSeconds: parsedEndTime,
          },
        });

        setDownloadState({
          status: "processing",
          workflowExecutionId,
          workflowState: "QUEUED",
        });

        const pollResult = await pollUntilComplete(workflowExecutionId);

        if (pollResult.status === "completed") {
          setDownloadState({
            status: "completed",
            workflowExecutionId,
            result: pollResult.result,
          });
          toast.success("YouTube download completed");
          return;
        }

        setDownloadState({
          status: "failed",
          workflowExecutionId,
          error: pollResult.error,
        });
        toast.error(pollResult.error);
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to run workflow";
        setDownloadState({ status: "failed", error: message });
        toast.error(message);
      }
    },
    [endTimeSeconds, pollUntilComplete, startTimeSeconds, submitWorkflow, youtubeUrl]
  );

  const handleReset = useCallback(() => {
    setYoutubeUrl("");
    setStartTimeSeconds("");
    setEndTimeSeconds("");
    setDownloadState({ status: "idle" });
    setLatestPollResponse(null);
  }, []);

  const workflowExecutionId =
    downloadState.status === "processing" ||
    downloadState.status === "completed" ||
    downloadState.status === "failed"
      ? downloadState.workflowExecutionId
      : undefined;

  const downloadUrl =
    downloadState.status === "completed"
      ? (downloadState.result.signedUrl ?? buildAssetUrl(downloadState.result.assetId))
      : null;

  const statusLabel = useMemo(() => {
    if (downloadState.status === "submitting") {
      return "Submitting workflow...";
    }
    if (downloadState.status === "processing") {
      return `Workflow state: ${downloadState.workflowState}`;
    }
    if (downloadState.status === "completed") {
      return "Workflow completed";
    }
    if (downloadState.status === "failed") {
      return "Workflow failed";
    }
    return "Ready";
  }, [downloadState]);

  return (
    <main className="w-full max-w-4xl space-y-6 p-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold">YouTube Download</h1>
        <p className="text-sm text-muted-foreground">
          Submit the youtube-ingest workflow, poll until completion, and download the ingested
          video.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Workflow Input</CardTitle>
          <CardDescription>Provide a YouTube URL and optional time range.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="youtube-url">YouTube URL</Label>
              <Input
                id="youtube-url"
                placeholder="https://www.youtube.com/watch?v=..."
                value={youtubeUrl}
                onChange={(event) => setYoutubeUrl(event.target.value)}
                disabled={isWorking}
                required
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="start-time">Start time (seconds)</Label>
                <Input
                  id="start-time"
                  type="number"
                  min={0}
                  step={0.1}
                  value={startTimeSeconds}
                  onChange={(event) => setStartTimeSeconds(event.target.value)}
                  disabled={isWorking}
                  placeholder="Optional"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-time">End time (seconds)</Label>
                <Input
                  id="end-time"
                  type="number"
                  min={0}
                  step={0.1}
                  value={endTimeSeconds}
                  onChange={(event) => setEndTimeSeconds(event.target.value)}
                  disabled={isWorking}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button type="submit" disabled={!canSubmit || submitPending}>
                {isWorking || submitPending ? "Running..." : "Start Download"}
              </Button>
              <Button type="button" variant="outline" onClick={handleReset} disabled={isWorking}>
                Reset
              </Button>
              <span className="text-sm text-muted-foreground">{statusLabel}</span>
            </div>
          </form>
        </CardContent>
      </Card>

      {workflowExecutionId && (
        <Card>
          <CardHeader>
            <CardTitle>Workflow Execution</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs font-mono break-all">{workflowExecutionId}</p>
          </CardContent>
        </Card>
      )}

      {downloadState.status === "failed" && (
        <Card className="border-destructive/60">
          <CardHeader>
            <CardTitle className="text-destructive">Download failed</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{downloadState.error}</p>
          </CardContent>
        </Card>
      )}

      {downloadState.status === "completed" && (
        <Card className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30">
          <CardHeader>
            <CardTitle className="text-green-900 dark:text-green-100">Download complete</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-[160px_1fr]">
              <div className="overflow-hidden rounded-lg border bg-background">
                <img
                  src={downloadState.result.metadata.thumbnailUrl}
                  alt={downloadState.result.metadata.title}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="space-y-2">
                <p className="text-base font-semibold">{downloadState.result.metadata.title}</p>
                <p className="text-sm text-muted-foreground">
                  Duration: {downloadState.result.metadata.durationSeconds.toFixed(1)}s
                </p>
                <p className="text-sm text-muted-foreground">
                  Dimensions: {downloadState.result.metadata.width ?? "?"} x{" "}
                  {downloadState.result.metadata.height ?? "?"}
                </p>
                <p className="text-xs font-mono break-all">{downloadState.result.assetId}</p>
                {downloadState.result.signedUrl && (
                  <p className="text-xs font-mono break-all">{downloadState.result.signedUrl}</p>
                )}
                <div className="flex flex-wrap gap-2">
                  {downloadUrl ? (
                    <Button asChild>
                      <a href={downloadUrl} target="_blank" rel="noreferrer">
                        Download Video
                      </a>
                    </Button>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No download URL returned by the workflow.
                    </p>
                  )}
                  {downloadState.result.signedUrl && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        const signedUrl = downloadState.result.signedUrl;
                        if (!signedUrl) {
                          return;
                        }
                        navigator.clipboard.writeText(signedUrl);
                        toast.success("Signed URL copied");
                      }}
                    >
                      Copy Signed URL
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(downloadState.result.assetId);
                      toast.success("Asset ID copied");
                    }}
                  >
                    Copy Asset ID
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {latestPollResponse && (
        <Card>
          <CardHeader>
            <CardTitle>Latest Poll Response</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="max-h-96 overflow-auto rounded-lg bg-muted p-4 text-xs">
              <code>{JSON.stringify(latestPollResponse, null, 2)}</code>
            </pre>
          </CardContent>
        </Card>
      )}
    </main>
  );
}
